import {AbstractStore} from "./abstract-store";
import {action, autorun, computed, makeObservable, observable} from "mobx";
import {ApiLayerConfiguration} from "../config/auftragsverwaltung-api-configuration";
import {JsonSerializer, throwError} from "typescript-json-serializer";
import {ProcessLog, ProcessLogProps} from "../models/process-log";

export interface ProcessLogStoreProps {
    layerConfig: ApiLayerConfiguration;
}

export class ProcessLogStore extends AbstractStore {

    /**
     * Loaded items (local store)
     */
    items = observable.map<string, ProcessLog>();

    constructor(props: ProcessLogStoreProps) {
        super({ layerConfig: props.layerConfig });

        makeObservable(this, {
            status: observable,
            dataStatus: observable,

            items: observable,
            selectedItem: observable,
            setSelectedItem: action,
            resetSelectedItem: action,
            visibleItems: computed,

            addItem: action,
            removeItem: action,

            isDataLoading: computed,
            isDataLoaded: computed,
            isDataError: computed,
            isLoading: computed,
            isLoaded: computed,
            isNotFound: computed,
            isError: computed
        });
    }

    get visibleItems(): ProcessLog[] {
        // apply filters
        const filtered = [...this.items.values()].filter(() => {
            return true;
        });
        // sort
        // filtered.sort(ProcessLog.sortByDate);
        return [ ...filtered];
    }

    logStatusChange(projectId: number, statusFrom: number, statusTo: number, user: string): void {
        const log = new ProcessLog({
            projectId,
            statusFrom,
            statusTo,
            timeStamp: new Date(),
            field: "STATUS_ID",
            message: "Status geändert",
            creator: user,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            createdAt: Date.now()
        });
        this.save(log, user).then(() => {
            // journal saved successfully
        }, (error) => {
            console.error("Error saving journal", error);
        });
    }

    /**
     * Override addItem. Use activityGuid as key
     * @param item One item or a map with multiple items with GUID as key
     */
    addItem(item: ProcessLog | Map<string, ProcessLog>): void {
        if (item instanceof Map) {
            // multiple items
            this.items.merge(item);
        }
        else {
            // one item
            if (this.items.has(item.guid)) {
                this.items.delete(item.guid);
            }
            this.items.set(item.guid, item);
        }
    }

    /**
     * Override removeItem. Use projectId as key
     * @param item
     */
    removeItem(item: ProcessLog): void {
        if (this.items.has(item.guid)) {
            this.items.delete(item.guid);
        }
    }

    loadItem(projectId: number | undefined): Promise<ProcessLog | null> {
        return new Promise((resolve, reject) => {
            this.setDataStatus("loading");

            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!projectId) {
                    reject();
                    return;
                }

                this.queryFeatures([projectId]).then((logs: ProcessLog[]) => {
                    if (logs[0] && logs[0].projectId === projectId) {
                        const log = logs[0];
                        const loadedLog = this.items.get(log.guid);
                        if (loadedLog) {
                            // return log as long as merging log is running. log can be displayed with
                            // indicator that synching is in log
                            // TODO: merge journal with already loaded activity
                        }

                        this.addItem(log);
                        this.setSelectedItem(log);
                        this.setDataStatus("loaded");

                        resolve(log);
                    } else {
                        this.setDataStatus("error");
                        reject();
                        throw new Error("Journal not found");
                    }
                });
            });
        });
    }

    /**
     * Request proccess logs from server and store in local store
     * @param projectIds (Optional) GUIDs of related activities
     */
    loadItems(projectIds?: number[]): Promise<ProcessLog[]> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                this.queryFeatures(projectIds).then((logs: ProcessLog[]) => {
                    if (logs) {
                        const map = new Map<string, ProcessLog>();
                        logs.forEach((l: ProcessLog) => {
                            map.set(l.guid, l);
                        });
                        this.addItem(map);
                        this.setDataStatus("loaded");

                        resolve(logs);
                    } else {
                        this.setDataStatus("error");
                        reject();
                        throw new Error("Error requesting journals");
                    }
                });
            });
        });
    }

    /**
     * Save journal to the server
     *
     * @param log
     * @param user
     */
    save(log: ProcessLog | null,
         user: string): Promise<ProcessLog | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!log) {
                    reject("Process log undefined when saving edits.");
                    return;
                }

                let feature = log?.feature;
                if (!feature) {
                    // new log --> create feature
                    feature = this.createFeature( null, log);
                }

                this.saveFeature(feature, "add", user).then((objectId: number) => {
                    const attrs = {};
                    attrs["objectId"] = objectId;
                    Object.assign(log, attrs);
                    if (log.feature) {
                        // update feature attributes also to allow deleting feature without page reload
                        log.feature.attributes["OBJECTID"] = objectId;
                    }

                    resolve(log);
                }, (error) => {
                    console.error("Error saving process log", error);
                    reject(error);
                });
            });
        });
    }

    /**
     * Delete a process log
     * @param log The process log to delete
     */
    delete(log: ProcessLog | null): Promise<number | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!log) {
                    reject("Process log undefined when deleting.");
                    return;
                }

                if (log.isNewRecord) {
                    reject("This process log is not saved. Does not have to be deleted.");
                    return;
                }

                const feature = log?.feature;
                if (!feature) {
                    reject(`Feature undefined for journal ${log.guid}`);
                    return;
                }

                this.deleteFeature(feature).then((objectId: number) => {

                    this.resetSelectedItem();
                    this.removeItem(log);

                    resolve(objectId);
                });
            });
        });
    }

    queryFeatures(projectIds?: number[]): Promise<ProcessLog[] | [] | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose();

                // Get a query object for the layer's current configuration
                // queryParams.outFields will be set to ["*"] to get values
                // for all available fields.
                const queryParams = this.layer.createQuery();

                // load all features if no journal guid is specified
                if (projectIds !== undefined) {
                    // Add to the layer's current definitionExpression
                    queryParams.where = queryParams.where + ` AND PROJ_ID IN (${projectIds.join("','")})`;
                }

                // query the layer with the modified params object
                this.layer.queryFeatures(queryParams).then((results: __esri.FeatureSet) => {
                    if (results.features) {
                        const logs = this.parseQueryResult(results);
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        resolve(logs);
                    } else {
                        reject();
                    }
                }, (error) => {
                    throw new Error(`Error querying process log + ${error}`);
                });
            });
        });
    }

    private parseQueryResult(data: __esri.FeatureSet): ProcessLog[] {
        const serializer = new JsonSerializer({
            errorCallback: throwError
        });

        const logs: ProcessLog[] = [];
        data.features?.forEach((feature: any) => {
            const json = serializer.deserialize(feature.attributes, ProcessLog);
            const log = new ProcessLog(json as ProcessLogProps);
            log.feature = feature;

            logs.push(log);
        });
        return logs;
    }

}
